import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProductProvider } from './context/ProductContext';
import { MainLayout } from './components/Layout';

// Pages
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import Proposals from './pages/Proposals';
import Deals from './pages/Deals';
import WalletPage from './pages/Wallet';
import SettingsPage from './pages/Settings';
import PendingActionsPage from './pages/PendingActions';
import UsageLogPage from './pages/UsageLog';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import SimulationRunner from './pages/SimulationRunner';
import PlatformDashboard from './pages/PlatformDashboard';
import { LoginPage, RegisterPage } from './pages/Auth';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return <MainLayout>{children}</MainLayout>;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (token) {
    return <Navigate to="/platform" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute><LoginPage /></PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute><RegisterPage /></PublicRoute>
      } />
      
      {/* Protected routes */}
      <Route path="/platform" element={
        <ProtectedRoute><PlatformDashboard /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/leads" element={
        <ProtectedRoute><Leads /></ProtectedRoute>
      } />
      <Route path="/leads/:id" element={
        <ProtectedRoute><LeadDetail /></ProtectedRoute>
      } />
      <Route path="/proposals" element={
        <ProtectedRoute><Proposals /></ProtectedRoute>
      } />
      <Route path="/deals" element={
        <ProtectedRoute><Deals /></ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute><WalletPage /></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <AdminRoute><SettingsPage /></AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/pending-actions" element={
        <ProtectedRoute>
          <AdminRoute><PendingActionsPage /></AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/usage-log" element={
        <ProtectedRoute>
          <AdminRoute><UsageLogPage /></AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/products" element={
        <ProtectedRoute><Products /></ProtectedRoute>
      } />
      <Route path="/products/:id" element={
        <ProtectedRoute><ProductDetail /></ProtectedRoute>
      } />
      <Route path="/simulations" element={
        <ProtectedRoute>
          <AdminRoute><SimulationRunner /></AdminRoute>
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/platform" replace />} />
      <Route path="*" element={<Navigate to="/platform" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProductProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'Inter, sans-serif'
              }
            }}
          />
        </BrowserRouter>
      </ProductProvider>
    </AuthProvider>
  );
}

export default App;
